import { useMemo, useState } from "react";
import { UserPlus, Users, Settings, Lock, Radio, Inbox } from "lucide-react";
import { useVault } from "@/context/VaultContext";
import { listSessions, preview } from "@/lib/selectors";
import { Avatar } from "@/components/Loader";
import { OrbitalMark } from "@/components/aph/OrbitalLock";
import { ConnectDialog } from "@/components/aph/ConnectDialog";
import { GroupDialog } from "@/components/aph/GroupDialog";
import { SettingsDialog } from "@/components/aph/SettingsDialog";
import { StoriesBar } from "@/components/aph/StoriesBar";
import { RequestsDialog } from "@/components/aph/RequestsDialog";
import { AlertsPrompt } from "@/components/aph/AlertsPrompt";

const RELAY = { connected: ["text-stellar", "Relay linked"], connecting: ["text-ash/50", "Linking…"], disconnected: ["text-flare", "Relay offline"] };

const IconBtn = ({ icon: Icon, onClick, testId, title }) => (
  <button onClick={onClick} data-testid={testId} title={title} className="p-2 rounded-secure text-ash/55 hover:text-stellar hover:bg-glass transition-colors"><Icon size={18} strokeWidth={1.5} /></button>
);

function SessionRow({ s, active, meCode, onClick }) {
  return (
    <button onClick={onClick} data-testid={`session-item-${s.session_id}`} className={`w-full flex items-center gap-3 px-4 py-3 text-left border-l-2 transition-colors ${active ? "bg-nebula border-stellar" : "border-transparent hover:bg-nebula/60"}`}>
      <div className="relative">
        <Avatar name={s.name} group={s.kind === "group"} />
        <span className="absolute -bottom-1 -right-1 bg-void rounded-full p-[1px]"><OrbitalMark className="w-4 h-4" status={s.lock} /></span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-branding font-semibold text-sm uppercase tracking-wider truncate">{s.name}</p>
        <p className={`text-xs truncate ${s.last?.status === "destroyed" ? "text-flare/70 italic" : "text-ash/45"}`}>{preview(s.last, meCode)}</p>
      </div>
      {s.unread > 0 && <span data-testid={`session-unread-${s.session_id}`} className="min-w-[20px] h-5 px-1.5 rounded-full bg-stellar text-void text-[11px] font-bold flex items-center justify-center">{s.unread}</span>}
    </button>
  );
}

export function Sidebar() {
  const { q, version, profile, activeSid, setActiveSid, relayStatus, lock } = useVault();
  const [dialog, setDialog] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const all = useMemo(() => listSessions(q, profile), [version, q, profile]);
  const sessions = all.filter((s) => !s.pending);
  const requests = all.filter((s) => s.pending === 1);
  const [tone, label] = RELAY[relayStatus];

  return (
    <aside className="w-full flex flex-col bg-void border-r border-glass" data-testid="sidebar">
      <div className="px-4 pt-5 pb-4 border-b border-glass">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5"><img src="/icon-192.png" alt="Aphelion" className="w-8 h-8 rounded-secure border border-glass" /><span className="font-branding font-bold tracking-[0.28em]">APHELION</span></div>
          <div className="flex items-center">
            <IconBtn icon={Settings} onClick={() => setDialog("settings")} testId="sidebar-settings-btn" title="Settings" />
            <IconBtn icon={Lock} onClick={lock} testId="sidebar-lock-btn" title="Lock vault" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" data-testid="current-alias">{profile.alias}</p>
            <p className={`text-[11px] font-mono flex items-center gap-1.5 ${tone}`} data-testid="relay-status" data-state={relayStatus}><Radio size={12} strokeWidth={1.5} /> {label}</p>
          </div>
          <div className="flex items-center gap-1">
            <IconBtn icon={UserPlus} onClick={() => setDialog("connect")} testId="sidebar-connect-btn" title="Connect" />
            <IconBtn icon={Users} onClick={() => setDialog("group")} testId="sidebar-group-btn" title="New group" />
          </div>
        </div>
      </div>
      <StoriesBar />
      <AlertsPrompt />
      {requests.length > 0 && (
        <button onClick={() => setDialog("requests")} data-testid="requests-btn" className="mx-3 mt-3 flex items-center justify-between px-3 py-2 rounded-secure border border-stellar/30 text-sm text-stellar hover:shadow-stellar-glow transition-shadow">
          <span className="flex items-center gap-2"><Inbox size={16} strokeWidth={1.5} /> Contact requests</span>
          <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-stellar text-void text-[11px] font-bold flex items-center justify-center" data-testid="requests-count">{requests.length}</span>
        </button>
      )}
      <div className="flex-1 overflow-y-auto py-1" data-testid="session-list">
        {sessions.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <p className="text-sm text-ash/45 mb-4">No channels yet.</p>
            <button onClick={() => setDialog("connect")} data-testid="empty-connect-btn" className="text-sm text-stellar border border-stellar/30 rounded-secure px-4 py-2 hover:shadow-stellar-glow transition-shadow">Connect via key</button>
          </div>
        ) : sessions.map((s) => <SessionRow key={s.session_id} s={s} meCode={profile.code} active={s.session_id === activeSid} onClick={() => setActiveSid(s.session_id)} />)}
      </div>
      <ConnectDialog open={dialog === "connect"} onOpenChange={(o) => setDialog(o ? "connect" : null)} />
      <GroupDialog open={dialog === "group"} onOpenChange={(o) => setDialog(o ? "group" : null)} />
      <RequestsDialog open={dialog === "requests"} onOpenChange={(o) => setDialog(o ? "requests" : null)} requests={requests} />
      <SettingsDialog open={dialog === "settings"} onOpenChange={(o) => setDialog(o ? "settings" : null)} />
    </aside>
  );
}
